import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import Chat from './components/Chat/Chat';
import EditFolder from './components/Folder/EditFolder/EditFolder';
import Folder from './components/Folder/Folder';
import PinnedChats from './components/Folder/PinnedChats';
import { ExpertProtections, NigelProtections } from './components/Providers/AuthProvider';
import PromptLibraryRoute, { LibraryRedirect } from './components/Routes/PromptLibraryRoute';
import Layout from './Layout';
import AdminPage from './pages/AdminPage';
import AdvancedSearch from './pages/AdvancedSearch';
import DealGPT from './pages/DealGPT/DealGPT';
import QuestionLibrary from './pages/DealGPT/QuestionLibrary';
import Deprecated from './pages/Deprecated';
import IntellioInsight from './pages/IntellioInsights';
import Login from './pages/Login';
import News from './pages/News';
import ProfileSettings from './pages/ProfileSettings';
import SharedConversation from './pages/SharedConversation';
import { selectAppStatus } from './redux/reducers/appStatusReducer';
import { AppEnum, FilesTabs, FolderView, ProjectConfigView } from './Types/enums';

interface ProtectedElementProps {
  element: React.ReactElement;
  protectionType?: 'Nigel' | 'Expert';
}

const ProtectedElement: React.FC<ProtectedElementProps> = ({ protectionType, element }) => {
  const ProtectionWrapper =
    protectionType === 'Nigel'
      ? NigelProtections
      : protectionType === 'Expert'
      ? ExpertProtections
      : Fragment;

  return <ProtectionWrapper>{element}</ProtectionWrapper>;
};

interface AppRouterProps {
  isExperts: boolean;
}

interface ProtectedRouteProps extends ProtectedElementProps {
  path: string;
}

const LegacyAiAppStoreRedirect: React.FC = () => {
  const { folderId = 'chat' } = useParams();

  return <Navigate to={`/folder/${folderId}/prompts`} replace />;
};

const AppRouter: React.FC<AppRouterProps> = ({ isExperts }) => {
  const { selectedApp } = useSelector(selectAppStatus);
  const routes = useMemo(
    () =>
      [
        {
          path: '/chat/shared/:conversationId',
          element: <SharedConversation />,
          protectionType: 'Nigel',
        },
        {
          path: '/search',
          element: <AdvancedSearch />,
          protectionType: 'Nigel',
        },
        {
          path: '/search/:filter',
          element: <AdvancedSearch />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/create',
          element: <EditFolder />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/chat',
          element: <Folder selectedView={FolderView.CHATS} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/chat/prompts/:view?',
          element: <PromptLibraryRoute />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/new-chat',
          element: <Folder selectedView={FolderView.CHAT} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/chat',
          element: <Folder selectedView={FolderView.CHATS} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/:conversationId',
          element: <Folder selectedView={FolderView.CHAT} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId',
          element: <Folder selectedView={FolderView.CHATS} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/prompts/:view?',
          element: <Folder selectedView={FolderView.PROMPTS} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/files',
          element: <Folder selectedView={FolderView.FILES} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/files/index',
          element: <Folder selectedView={FolderView.FILES} filesView={FilesTabs.INDEXED} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/files/graph',
          element: (
            <Folder selectedView={FolderView.FILES} filesView={FilesTabs.GRAPH_RAG_VISUALIZATION} />
          ),
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/ai-appstore',
          element: <LegacyAiAppStoreRedirect />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/discovery',
          element: <Folder selectedView={FolderView.DISCOVERY} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/data',
          element: <Folder selectedView={FolderView.DATA_PROCESSING} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/data-request-list',
          element: <Folder selectedView={FolderView.DATA_REQUEST_LIST} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/analysis',
          element: <Folder selectedView={FolderView.SCOPE_AREAS} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/analysis/:scope',
          element: <Folder selectedView={FolderView.ANALYSIS} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/edit',
          element: <Folder selectedView={FolderView.EDIT} />,
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/configure',
          element: (
            <Folder selectedView={FolderView.CONFIG} configView={ProjectConfigView.CONFIG} />
          ),
          protectionType: 'Nigel',
        },
        {
          path: '/folder/:folderId/configure/kpi',
          element: <Folder selectedView={FolderView.CONFIG} configView={ProjectConfigView.KPI} />,
          protectionType: 'Nigel',
        },
        {
          path: '/pinned-chats',
          element: <PinnedChats />,
          protectionType: 'Nigel',
        },
        {
          path: '/news',
          element: <News />,
          protectionType: 'Nigel',
        },
        {
          path: '/admin-page',
          element: <AdminPage />,
          protectionType: 'Nigel',
        },
        {
          path: '/admin-page/:subroute',
          element: <AdminPage />,
          protectionType: 'Nigel',
        },
        {
          path: '/profile-settings',
          element: <ProfileSettings />,
          protectionType: 'Nigel',
        },
        {
          path: '/temporary-chat',
          element: <Chat isTemporary />,
          protectionType: 'Nigel',
        },
        {
          path: '/question-library',
          element: <QuestionLibrary />,
          protectionType: 'Nigel',
        },
        {
          path: '/intellio-advantage',
          element: <DealGPT />,
          protectionType: 'Nigel',
        },
        {
          path: '/account-coach/interview',
          element: <Deprecated />,
          protectionType: 'Expert',
        },
        {
          path: '/account-coach/upload',
          element: <Deprecated />,
          protectionType: 'Expert',
        },
        {
          path: '/assistants',
          element: <Deprecated />,
          protectionType: 'Expert',
        },
        {
          path: '/assistants/:assistantId',
          element: <Deprecated />,
          protectionType: 'Expert',
        },
        {
          path: '/intellio-insights',
          element: <IntellioInsight />,
          protectionType: 'Nigel',
        },
      ] as ProtectedRouteProps[],
    []
  );

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          isExperts ? (
            <Navigate to="/account-coach/interview" replace={true} />
          ) : (
            <LibraryRedirect
              fallbackElement={<Navigate to="/folder/chat?ui=classic" replace={true} />}
            />
          )
        }
      />
      {isExperts ? (
        <>
          {routes.map(({ path, element, protectionType }) => (
            <Route
              key={path}
              path={path}
              element={<ProtectedElement element={element} protectionType={protectionType} />}
            />
          ))}
        </>
      ) : (
        <Route element={<ProtectedElement element={<Layout />} protectionType={'Nigel'} />}>
          {routes.map(({ path, element, protectionType }) => (
            <Route
              key={path}
              path={path}
              element={<ProtectedElement element={element} protectionType={protectionType} />}
            />
          ))}
        </Route>
      )}

      <Route
        path="*"
        element={
          isExperts ? (
            <Navigate to="/account-coach/interview" replace={true} />
          ) : selectedApp === AppEnum.DEAL_GPT ? (
            <Navigate to="/intellio-advantage" replace={true} />
          ) : (
            <Navigate to="/folder/chat" replace={true} />
          )
        }
      />
    </Routes>
  );
};

export default AppRouter;
